

import React, { FC, useMemo, useEffect, useState } from "react";
import Modal from "@/components/Modal";
import { useAppTheme } from "@/utils/theme";
import { Box, Grid, Typography, TextField, IconButton, Tooltip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import Datepicker from "@/components/Datepicker";
import Timepicker from "@/components/Timepicker";
import Select from "@/components/Select";
import Button from "@/components/Button";
import appointmentValidationSchema, {
  AppointmentFormData,
} from "./validation.schema";
import { Appointment, AppointmentStatusEnum, AppointmentTypeEnum, AppointmentMeetingTypeEnum } from "@/domain/appointment.interface";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAppointment, useUpdateAppointment } from "@/hooks/useAppointments";
import AppointmentsService from "@/services/appointments.service";
import TreatmentAgenciesService, { TreatmentAgencyAddress } from "@/services/treatment-agencies.service";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { computeDefaultTimes } from "@/utils/dateUtils";

dayjs.extend(utc);

type AppointmentModalMode = "create" | "edit";

type AppointmentModalProps = {
  open: boolean;
  mode: AppointmentModalMode;
  onClose: () => void;
  onSaved?: () => void;
  participantId: string;
  enrollmentId: string;
  programId: string;
  appointment?: Appointment | null;
};

const AppointmentModal: FC<AppointmentModalProps> = ({
  open,
  mode,
  onClose,
  onSaved,
  participantId,
  enrollmentId,
  programId,
  appointment,
}) => {
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const { showSnackBar, showSimpleAlert } = useFeedBack();

  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const [appointmentReasons, setAppointmentReasons] = useState<{ key: string; value: string }[]>([]);
  const [treatmentAgencies, setTreatmentAgencies] = useState<{ key: string; value: string }[]>([]);
  const [addresses, setAddresses] = useState<TreatmentAgencyAddress[]>([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [skipPastValidation, setSkipPastValidation] = useState(mode === "edit");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors },
    setValue,
  } = useForm<AppointmentFormData>({
    resolver: yupResolver<AppointmentFormData>(appointmentValidationSchema, {
      context: { skipPastValidation },
    }),
    context: { skipPastValidation },
    mode: "all",
    defaultValues: {
      subject: "",
      ...computeDefaultTimes(),
      appointmentReasonId: "",
      treatmentAgencyId: "",
      locationId: "",
      contactName: "",
    },
  });

  const selectedAgencyId = watch("treatmentAgencyId");
  const selectedAddressId = watch("locationId");
  const watchedStartTime = watch("startTime");
  const watchedEndTime = watch("endTime");
  const watchedDate = watch("date");
  const selectedAddress = useMemo(
    () => addresses.find((addr) => addr._id === selectedAddressId) || null,
    [addresses, selectedAddressId]
  );

  const addressOptions = useMemo(
    () =>
      addresses.map((address) => ({
        key: address._id,
        value: address.name,
      })),
    [addresses]
  );

  useEffect(() => {
    if (mode === "edit" && appointment) {

      const start = dayjs.utc(appointment.startDate);
      const end = dayjs.utc(appointment.endDate);
      reset({
        subject: appointment.subject || "",
        date: start,
        startTime: start,
        endTime: end,
        appointmentReasonId: appointment.appointmentReasonId || "",
        treatmentAgencyId: appointment.treatmentAgencyId || "",
        locationId: appointment.locationId || "",
        contactName: appointment.contactName || "",
      });
      setSkipPastValidation(true);
    } else if (mode === "create") {
      const defaults = computeDefaultTimes();
      reset({
        subject: "",
        date: defaults.date,
        startTime: defaults.startTime,
        endTime: defaults.endTime,
        appointmentReasonId: "",
        treatmentAgencyId: "",
        locationId: "",
        contactName: "",
      });
      setSkipPastValidation(false);
    }
  }, [mode, appointment, reset, open]);

  useEffect(() => {
    setSkipPastValidation(mode === "edit");
  }, [mode]);

  const handleClose = () => {
    if (createAppointment.isPending || updateAppointment.isPending) return;
    onClose();
  };

  const onSubmit = async (data: AppointmentFormData) => {
    const buildDateTime = (baseDate: Dayjs, time: Dayjs) =>
      dayjs(baseDate)
        .hour(dayjs(time).hour())
        .minute(dayjs(time).minute())
        .second(0)
        .millisecond(0);

    // Build times in the user-selected clock time, no timezone conversion.
    const startDateTime = buildDateTime(data.date as Dayjs, data.startTime as Dayjs);
    const endDateTime = buildDateTime(data.date as Dayjs, data.endTime as Dayjs);

    const payload = {
      subject: data.subject,
      appointmentType: AppointmentTypeEnum.PARTICIPANT_OWNER,
      meetingType: AppointmentMeetingTypeEnum.INDIVIDUAL,
      startDate: startDateTime.format("YYYY-MM-DDTHH:mm:ss[Z]"),
      endDate: endDateTime.format("YYYY-MM-DDTHH:mm:ss[Z]"),
      status: AppointmentStatusEnum.SCHEDULED,
      programId,
      participantsId: [participantId],
      organizerId: userInfo?._id as string,
      appointmentReasonId: data.appointmentReasonId || undefined,
      treatmentAgencyId: data.treatmentAgencyId,
      locationId: data.locationId,
      contactName: data.contactName?.trim(),
    };

    try {

      if (mode === "create") {
        await createAppointment.mutateAsync({
          enrollmentId,
          userId: userInfo?._id as string,
          payload,
        });
        showSnackBar("Appointment created successfully", "success");
      } else if (mode === "edit" && appointment) {
        await updateAppointment.mutateAsync({
          enrollmentId,
          appointmentId: appointment._id,
          userId: userInfo?._id as string,
          payload,
        });
        showSnackBar("Appointment updated successfully", "success");
      }
      handleClose();
      onSaved?.();
    } catch (error: any) {
      showSimpleAlert(
        error?.message ||
          error?.error ||
          "There was a problem saving the appointment."
      );
    }
  };

  useEffect(() => {
    const loadAppointmentReasons = async () => {
      if (!userInfo?._id || !userInfo.accountId || !open) return;
      setIsLoadingReasons(true);
      try {
        const response = await AppointmentsService.getAppointmentReasons(
          userInfo.accountId,
          userInfo._id
        );
        const options =
          response?.flatMap((reason) =>
            reason.categories?.map((category) => ({
              key: category._id,
              value: category.languages?.[0]?.name || "Reason",
            })) || []
          ) || [];
        setAppointmentReasons(options);
      } catch (error) {
        console.error("Error fetching appointment reasons", error);
      } finally {
        setIsLoadingReasons(false);
      }
    };

    loadAppointmentReasons();
  }, [userInfo?._id, userInfo?.accountId, open]);

  useEffect(() => {
    const loadTreatmentAgencies = async () => {
      if (!userInfo?._id || !userInfo.accountId || !open) return;
      setIsLoadingAgencies(true);
      try {
        const { agencies } = await TreatmentAgenciesService.getTreatmentAgencies(
          { accountId: userInfo.accountId, userId: userInfo._id }
        );
        const options = agencies.map((agency) => ({
          key: agency._id,
          value: agency.name,
        }));
        setTreatmentAgencies(options);
      } catch (error) {
        console.error("Error fetching treatment agencies", error);
      } finally {
        setIsLoadingAgencies(false);
      }
    };

    loadTreatmentAgencies();
  }, [userInfo?._id, userInfo?.accountId, open]);

  useEffect(() => {
    const loadAddresses = async () => {
      if (!userInfo?._id || !userInfo.accountId || !selectedAgencyId) {
        setAddresses([]);
        return;
      }
      setIsLoadingAddresses(true);
      try {
        const { addresses } =
          await TreatmentAgenciesService.getTreatmentAgencyAddresses({
            accountId: userInfo.accountId,
            userId: userInfo._id,
            agencyId: selectedAgencyId,
          });
        setAddresses(addresses);
      } catch (error) {
        console.error("Error fetching treatment agency addresses", error);
        setAddresses([]);
      } finally {
        setIsLoadingAddresses(false);
      }
    };

    loadAddresses();
  }, [selectedAgencyId, userInfo?._id, userInfo?.accountId]);

  // Revalidate endTime whenever startTime changes to clear stale errors and ensure ordering.
  useEffect(() => {
    if (watchedEndTime) {
      trigger("endTime");
    }
  }, [watchedStartTime, watchedEndTime, trigger]);

  // Revalidate time fields when date changes (clears past-time errors if date moves to future).
  useEffect(() => {
    if (watchedDate) {
      trigger(["startTime", "endTime"]);
    }
  }, [watchedDate, trigger]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      isPending={createAppointment.isPending || updateAppointment.isPending}
      title="Appointment"
      theme={theme}
      size={{
        width: "100%",
        minHeight: 460,
        maxHeight: 820,
        maxWidth: 760,
      }}
      content={
        <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12}>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Reason for Appointment"
                    placeholder="e.g. First meeting"
                    error={!!errors.subject}
                    helperText={errors.subject?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <Datepicker
                    {...field}
                    label="Date"
                    value={field.value as Dayjs}
                    onChange={(value) => {
                      setSkipPastValidation(false);
                      field.onChange(value);
                    }}
                    error={!!errors.date}
                    helperText={errors.date?.message}
                    required
                    disableFuture={false}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <Timepicker
                    {...field}
                    label="Start time"
                    value={field.value as Dayjs | null}
                    onChange={(value) => {
                      setSkipPastValidation(false);
                      field.onChange(value);
                      if (value) {
                        const newEnd = dayjs(value).add(30, "minute");
                        setValue("endTime", newEnd, { shouldValidate: true, shouldDirty: true });
                      }
                    }}
                    error={!!errors.startTime}
                    helperText={errors.startTime?.message}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <Timepicker
                    {...field}
                    label="End time"
                    value={field.value as Dayjs | null}
                    onChange={(value) => {
                      setSkipPastValidation(false);
                      field.onChange(value);
                    }}
                    error={!!errors.endTime}
                    helperText={errors.endTime?.message}
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="titleSmall"
                fontWeight={700}
                gutterBottom
                color={theme.palette.menu.dark}
              >
                Additional information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
              <Controller
                name="appointmentReasonId"
                control={control}
                render={({ field }) => (
                  <Select
                    handleChange={(value) => field.onChange(value)}
                    data={appointmentReasons}
                    value={field.value}
                    label="Reason"
                    required
                    id="appointment-reason"
                    fullWidth
                    disabled={isLoadingReasons}
                    error={!!errors.appointmentReasonId}
                    errorMessage={errors.appointmentReasonId?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <Controller
                name="treatmentAgencyId"
                control={control}
                render={({ field }) => (
                  <Select
                    handleChange={(value) => {
                      field.onChange(value);
                      // reset address if agency changes
                      setValue("locationId", "");
                      setValue("contactName", "");
                    }}
                    data={treatmentAgencies}
                    value={field.value}
                    label="Treatment Agency"
                    required
                    id="appointment-treatment-agency"
                    fullWidth
                    disabled={isLoadingAgencies}
                    error={!!errors.treatmentAgencyId}
                    errorMessage={errors.treatmentAgencyId?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <Controller
                name="locationId"
                control={control}
                render={({ field }) => (
                  <Select
                    handleChange={(value) => field.onChange(value)}
                    data={addressOptions}
                    value={field.value}
                    label="Address"
                    required
                    id="appointment-address"
                    fullWidth
                    disabled={!selectedAgencyId || isLoadingAddresses}
                    error={!!errors.locationId}
                    errorMessage={errors.locationId?.message}
                  />
                )}
              />
            </Grid>

            {selectedAddress ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box>
                    {selectedAddress.email && (
                      <Typography variant="body2" color="text.primary">
                        Email: {selectedAddress.email}
                      </Typography>
                    )}
                    {selectedAddress.phone && (
                      <Typography variant="body2" color="text.primary">
                        Phone: {selectedAddress.phone}
                      </Typography>
                    )}
                  </Box>
                  {selectedAddress.latitude && selectedAddress.longitude ? (
                    <Tooltip title="Open in Google Maps">
                      <IconButton
                        size="small"
                        onClick={() =>
                          window.open(
                            `https://www.google.com/maps/search/?api=1&query=${selectedAddress.latitude},${selectedAddress.longitude}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <PlaceOutlinedIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <Controller
                name="contactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Contact"
                    disabled={!selectedAddressId}
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button variantType="cancel" onClick={handleClose} label="CANCEL" />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            isLoading={createAppointment.isPending || updateAppointment.isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default AppointmentModal;
